import React, { useEffect, useMemo, useState } from "react";
import { convertInrAmount, formatMoney, readStoredCurrencyContext } from "../utils/currency";
import { API_BASE, PLAN_TABS, buildFallbackCatalog, getPricingMode, normalizePricingResponse, normalizePrinters, readRestaurantProfile } from "../utils/planPricing";

const GOLD = "#015B9A";
const GOLD_DEEP = "#034A7A";
const DARK = "#0b1020";

const FEATURE_MATRIX_BASE = [
  { key: "addCore", basic: true, starter: true, professional: true },
  { key: "posQr", basic: true, starter: true, professional: true },
  { key: "fastBilling", basic: true, starter: true, professional: true },
  { key: "kitchenDisplay", basic: true, starter: true, professional: true },
  { key: "aiImages", basic: true, starter: true, professional: true },
  { key: "scanMenu", basic: true, starter: true, professional: true },
  { key: "staffRoles", basic: false, starter: true, professional: true },
  { key: "liveSync", basic: false, starter: true, professional: true },
  { key: "finance", basic: false, starter: true, professional: true },
  { key: "reports", basic: false, starter: true, professional: true },
  { key: "customWorkflow", basic: false, starter: true, professional: true },
  { key: "inventory", basic: false, starter: true, professional: true },
  { key: "billViaQr", basic: false, starter: true, professional: true },
  { key: "webAccess", basic: false, starter: false, professional: true },
];

const FEATURE_ICON_MAP = {
  unifiedPosQr: "✨",
  productImages: "🖼️",
  liveKot: "🔥",
  inventory: "📦",
  scanMenu: "📷",
  billViaQr: "🔳",
  liveSync: "🔁",
  fastBilling: "⚡",
  smartFinance: "📊",
  staffAudit: "🛡️",
  incomeExpense: "📒",
  reports: "📈",
  support: "🎧",
  platforms: "📱",
};

const COPY = {
  title: "Our Plans",
  premiumBadge: "Premium Experience",
  reliable: "Reliable, always-on",
  heroTitle: "Luxury-Grade Restaurant Management Software",
  heroBody:
    "Modern POS, QR dine-in, kitchen management, income/expense & reports — now with a Professional plan that includes Web access (without printer).",
  demo: "Book a demo",
  call: "Schedule a call",
  pickLane: "Choose a Plan",
  featureComparison: "Feature comparison",
  featureComparisonSub: "Compare Basic, Starter, and Professional benefits",
  execution: "Execution toolkit",
  executionSub: "What your team gets on day one",
  customWorkflows: "Custom workflows",
  customBody:
    "Need franchise-ready rollouts, unique menu flows or integrations? We design it, ship updates after launch and train your floor staff.",
  requestCustomBuild: "Request a custom build",
  featureMatrix: {
    addCore: "Add products/categories/tables/orders",
    posQr: "POS + QR basics",
    fastBilling: "Fast billing shortcuts",
    kitchenDisplay: "Kitchen display + auto print",
    aiImages: "AI product images",
    scanMenu: "Scan menu with AI",
    staffRoles: "Staff roles (Owner/Cashier/Waiter/Chef)",
    liveSync: "Live sync (owner & staff)",
    finance: "Finance, income/expense & Khatabook",
    reports: "Day / month / waiter / table reports",
    customWorkflow: "Custom workflows & training",
    inventory: "Inventory",
    billViaQr: "Bill / order via QR codes",
    webAccess: "Web dashboard access (no printer)",
  },
  features: {
    unifiedPosQr: {
      title: "Unified POS + QR",
      subtitle: "POS, dine-in, takeaways and QR table orders on one lightning panel.",
    },
    productImages: {
      title: "Product images with AI",
      subtitle: "Instantly generate crisp food photos and keep your menu glossy.",
    },
    liveKot: {
      title: "Live KOT & Kitchen",
      subtitle: "Fire KOTs, track statuses, print tokens and avoid double-prep.",
    },
    inventory: {
      title: "Inventory + Costing",
      subtitle: "Real-time stock, recipe costs and variance reports in one tap.",
    },
    scanMenu: {
      title: "Scan menu with AI",
      subtitle: "Upload/scan menus and get dishes, prices and tags auto-created.",
    },
    billViaQr: {
      title: "Bill via QR codes",
      subtitle: "Guests scan, order and pay with table-wise QR codes.",
    },
    liveSync: {
      title: "Live sync for teams",
      subtitle: "Owner, cashier, chef and waiter stay synced in real time.",
    },
    fastBilling: {
      title: "Fast billing",
      subtitle: "3-tap quick bills with saved combos, offers and payment modes.",
    },
    smartFinance: {
      title: "Smart Finance",
      subtitle: "GST-ready billing, expenses and payment insights for owners.",
    },
    staffAudit: {
      title: "Staff & Audit",
      subtitle: "Roles, approvals, clock-ins and a clear log of who did what.",
    },
    incomeExpense: {
      title: "Income, expense & Khatabook",
      subtitle: "Track cashflows, credit, and ledger history without spreadsheets.",
    },
    reports: {
      title: "Reports that matter",
      subtitle: "Day/month summaries, waiter & table wise insights, exports.",
    },
    support: {
      title: "Support that ships",
      subtitle: "Playbook-driven onboarding with updates shipped after go-live.",
    },
    platforms: {
      title: "Android & iOS (web coming soon)",
      subtitle: "Run outlets on mobile today; web back-office arrives soon.",
    },
  },
};

const formatINR = (n) => {
  const context = readStoredCurrencyContext();
  return formatMoney(convertInrAmount(n, context), context, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const sendWhatsApp = (text) => {
  const phone = "9988777409";
  const msg = `Hi, I would like to know more about ${text}.`;
  const url = `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const callSales = () => {
  const phone = "+919988777409";
  window.location.href = `tel:${phone}`;
};

const PaymentModal = ({ open, onClose, selectedPrice, onWhatsApp, discount = 0, couponCode = "" }) => {
  if (!open || !selectedPrice) return null;
  const { planName, durationLabel, amount, printerLabel, printerAmount } =
    selectedPrice;
  const totalAmount = Math.max(0, Number(amount || 0) + Number(printerAmount || 0) - Number(discount || 0));
  return (
    <div style={sx.pmOverlay} onClick={onClose}>
      <div style={sx.pmSheet} onClick={(e) => e.stopPropagation()}>
        <div style={sx.pmHandle} />
        <div style={sx.pmHeader}>
          <div style={sx.pmIcon}>🔒</div>
          <div style={{ flex: 1 }}>
            <div style={sx.pmTitle}>Confirm & Pay</div>
            <div style={sx.pmSub}>You selected {planName} • {durationLabel}</div>
          </div>
          <button style={sx.pmClose} onClick={onClose}>✕</button>
        </div>

        <div style={sx.pmSummary}>
          <div style={{ flex: 1 }}>
            <div style={sx.pmPlan}>{planName} Plan</div>
            <div style={sx.pmMeta}>Secure support • Assisted setup</div>
            {printerLabel ? (
              <div style={sx.pmMeta}>Printer: {printerLabel}</div>
            ) : null}
            {couponCode ? <div style={sx.pmMeta}>Coupon {couponCode}: -{formatINR(discount)}</div> : null}
          </div>
          <div style={sx.pmAmountWrap}>
            <div style={sx.pmAmountLabel}>Payable</div>
            <div style={sx.pmAmount}>{formatINR(totalAmount)}</div>
          </div>
        </div>

        <button
          style={{ ...sx.pmPrimaryBtn, marginTop: 12 }}
          onClick={onWhatsApp}
        >
          💬 Send details on WhatsApp
        </button>

        <div style={sx.pmBtnRow}>
          <button style={sx.pmSecondaryBtn} onClick={callSales}>📞 Call Sales</button>
          <button style={sx.pmGhostBtn} onClick={onClose}>Not Now</button>
        </div>
      </div>
    </div>
  );
};

const PlanShowcase = () => {
  const profile = useMemo(() => readRestaurantProfile(), []);
  const restaurantId = Number(profile?.id || profile?.restaurant_id || 0);
  const fallbackMode = getPricingMode(restaurantId);
  const [activeTab, setActiveTab] = useState("starter");
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [printerOption, setPrinterOption] = useState("app");
  const [pricingCatalog, setPricingCatalog] = useState(() => buildFallbackCatalog(fallbackMode));
  const [printers, setPrinters] = useState(() => normalizePrinters({}));
  const [pricingLoading, setPricingLoading] = useState(true);
  const [couponsEnabled, setCouponsEnabled] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    let alive = true;
    const loadPricing = async () => {
      if (!restaurantId) {
        if (alive) setPricingLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/plan-showcase/pricing?restaurantid=${restaurantId}`);
        const result = await response.json();
        if (!response.ok || !result?.success) throw new Error(result?.message || "Pricing unavailable");
        if (!alive) return;
        setPricingCatalog(normalizePricingResponse(result, fallbackMode));
        setPrinters(normalizePrinters(result));
        setCouponsEnabled(Boolean(result?.coupons_enabled));
      } catch (error) {
        console.warn("Using plan pricing fallback:", error);
      } finally {
        if (alive) setPricingLoading(false);
      }
    };
    loadPricing();
    return () => { alive = false; };
  }, [fallbackMode, restaurantId]);

  const featureMatrix = useMemo(
    () =>
      FEATURE_MATRIX_BASE.map((row) => ({
        ...row,
        title: COPY.featureMatrix[row.key] || row.key,
      })),
    []
  );

  const features = useMemo(
    () =>
      Object.entries(FEATURE_ICON_MAP).map(([key, icon]) => ({
        key,
        icon,
        title: COPY.features?.[key]?.title || key,
        subtitle: COPY.features?.[key]?.subtitle || "",
      })),
    []
  );

  const activePlanName =
    activeTab === "basic"
      ? "Basic"
      : activeTab === "starter"
      ? "Starter"
      : activeTab === "professional"
      ? "Professional"
      : "Starter";
  const pricingData = pricingCatalog?.plans?.[activeTab] || { baseYear: 0, items: [] };
  const extensionOptions = (pricingCatalog?.extensionOptions || []).filter((row) => row.planKey === activeTab);
  const printerOptions = useMemo(() => [
    { key: "app", label: "App Only", amount: 0 },
    ...printers.map((printer) => ({ key: printer.id, label: printer.title, amount: printer.amount, specs: printer.specs })),
  ], [printers]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code || !selectedPrice || couponBusy) return;
    const printer = printerOptions.find((item) => item.key === printerOption) || printerOptions[0];
    setCouponBusy(true);
    try {
      const response = await fetch(`${API_BASE}/plan-showcase/coupon/validate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ coupon_code: code, restaurantid: restaurantId, plan_key: selectedPrice.planKey, duration_key: selectedPrice.durationKey, printer_id: printer.key === "app" ? undefined : printer.key, purchase_type: printer.key === "app" ? "plan" : "plan_printer" }),
      });
      const result = await response.json();
      if (!response.ok || !result?.success) throw new Error(result?.message || "Coupon is not valid");
      setAppliedCoupon({ code: result?.coupon?.code || code, discount: Number(result?.summary?.discount_inr || 0) });
    } catch (error) {
      setAppliedCoupon(null);
      alert(error?.message || "Coupon validation failed");
    } finally { setCouponBusy(false); }
  };

  const onSelectPrice = (row) => {
    setAppliedCoupon(null);
    setSelectedPrice({
      planKey: activeTab,
      planName: activePlanName,
      durationKey: row.durationKey,
      durationLabel: row.label,
      amount: row.amount,
    });
  };

  const sendSelectedPlanOnWhatsApp = () => {
    if (!selectedPrice) return;
    const printer = printerOptions.find((item) => item.key === printerOption) || printerOptions[0];
    const planAmount = Number(selectedPrice.amount || 0);
    const printerAmount = Number(printer.amount || 0);
    const discount = Number(appliedCoupon?.discount || 0);
    const total = Math.max(0, planAmount + printerAmount - discount);
    const ownerName = profile?.name || profile?.owner_name || "Not available";
    const restaurantName = profile?.restaurant_name || profile?.shop_name || "Not available";
    const mobile = profile?.mobile_number || profile?.phone || "Not available";
    const message = [
      "Hi Bill Sarthi Team,",
      "",
      "I want to purchase/renew the following plan:",
      "",
      `Restaurant ID: ${restaurantId || "Not available"}`,
      `Owner Name: ${ownerName}`,
      `Restaurant Name: ${restaurantName}`,
      `Registered Mobile: ${mobile}`,
      "",
      `Plan: ${selectedPrice.planName}`,
      `Duration: ${selectedPrice.durationLabel}`,
      `Plan Amount: ${formatINR(planAmount)}`,
      `Printer: ${printer.key === "app" ? "No printer (App only)" : printer.label}`,
      `Printer Amount: ${formatINR(printerAmount)}`,
      `Coupon: ${appliedCoupon?.code || "Not applied"}`,
      `Discount: ${formatINR(discount)}`,
      `Final Amount: ${formatINR(total)}`,
      "",
      "Please help me complete the purchase.",
    ].join("\n");
    const url = `https://wa.me/919988777409?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setPayModalOpen(false);
  };

  return (
    <div style={sx.page}>
      <div style={sx.glowOne} />
      <div style={sx.glowTwo} />

      <div style={sx.container}>
        <header style={sx.header}>
          <div style={sx.headerBadge}>🏅</div>
          <div>
            <div style={sx.headerTitle}>{COPY.title}</div>
            <div style={sx.headerSub}>Plans built for modern restaurants</div>
          </div>
        </header>

        <section style={sx.hero}>
          <div style={sx.heroTop}>
            <div style={sx.heroBadge}>✨ {COPY.premiumBadge}</div>
            <div style={sx.heroPill}>🛡️ {COPY.reliable}</div>
          </div>
          <div style={sx.heroTitle}>{COPY.heroTitle}</div>
          <div style={sx.heroBody}>{COPY.heroBody}</div>
          <div style={sx.heroNote}>
            Printer add-on prices are loaded live. Add one to any plan if needed.
          </div>
          <div style={sx.heroActions}>
            <button style={sx.primaryCta} onClick={() => sendWhatsApp(COPY.demo)}>
              💬 {COPY.demo}
            </button>
            <button style={sx.secondaryCta} onClick={callSales}>📞 {COPY.call}</button>
          </div>
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.pickLane}</div>
            <div style={sx.sectionSub}>Tap a plan, then select duration to continue.</div>
          </div>

          <div style={sx.tabsWrap}>
            {PLAN_TABS.map((tab) => {
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSelectedPrice(null);
                    setAppliedCoupon(null);
                  }}
                  style={{
                    ...sx.tabPill,
                    ...(active ? sx.tabPillActive : null),
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div style={sx.pricingCard}>
          <div style={sx.pricingTop}>
            <div>
              <div style={sx.pricingPlan}>{activePlanName} Plan</div>
              <div style={sx.pricingBase}>
                1 Year Base Price: <strong>{formatINR(pricingData.baseYear)}</strong>
              </div>
              {pricingLoading ? <div style={sx.pricingBase}>Loading prices…</div> : null}
            </div>
            {/* <div style={sx.bestValue}>Best Value</div> */}
          </div>

          <div style={sx.pricingHead}>
            <span>Duration</span>
            <span>Standard Price</span>
          </div>

          <div style={sx.pricingRows}>
            {pricingData.items.map((row) => {
              const isSelected =
                selectedPrice?.planKey === activeTab &&
                selectedPrice?.durationKey === row.durationKey;
              return (
                <button
                  key={row.durationKey}
                  onClick={() => onSelectPrice(row)}
                  style={{
                    ...sx.priceRow,
                    ...(isSelected ? sx.priceRowSelected : null),
                  }}
                >
                  <div style={sx.priceLeft}>
                    <span style={sx.radio}>{isSelected ? "●" : "○"}</span>
                    <span style={sx.priceDuration}>{row.label}</span>
                    {row.durationKey === "3y" ? (
                      <span style={sx.chipBest}>BEST VALUE</span>
                    ) : row.durationKey === "1y" ? (
                      <span style={sx.chipPopular}>POPULAR</span>
                    ) : null}
                  </div>
                  <div style={sx.priceRight}>
                    {row.originalAmount > row.amount ? <span style={sx.originalAmount}>{formatINR(row.originalAmount)}</span> : null}
                    <span style={sx.priceAmount}>{formatINR(row.amount)}</span>
                    <span style={sx.chev}>›</span>
                  </div>
                </button>
              );
            })}
          </div>
          {extensionOptions.length ? (
            <div style={sx.extensionWrap}>
              <div style={sx.printerTitle}>Monthly recharge / extension</div>
              <div style={sx.printerSub}>Short-duration options available for this plan.</div>
              <div style={sx.pricingRows}>
                {extensionOptions.map((row) => {
                  const isSelected = selectedPrice?.planKey === activeTab && selectedPrice?.durationKey === row.durationKey;
                  return <button key={`${row.planKey}-${row.durationKey}`} onClick={() => onSelectPrice(row)} style={{ ...sx.priceRow, ...(isSelected ? sx.priceRowSelected : null) }}>
                    <div style={sx.priceLeft}><span style={sx.radio}>{isSelected ? "●" : "○"}</span><span style={sx.priceDuration}>{row.label}</span></div>
                    <div style={sx.priceRight}>{row.originalAmount > row.amount ? <span style={sx.originalAmount}>{formatINR(row.originalAmount)}</span> : null}<span style={sx.priceAmount}>{formatINR(row.amount)}</span></div>
                  </button>;
                })}
              </div>
            </div>
          ) : null}
          </div>

          <div style={sx.printerCard}>
            <div style={sx.printerTitle}>Printer Option</div>
            <div style={sx.printerSub}>
              Select if you want a printer with this plan.
            </div>
            {!selectedPrice && (
              <div style={sx.printerHint}>Pick a duration to continue.</div>
            )}
            <div style={sx.printerOptions}>
              {printerOptions.map((opt) => {
                const active = printerOption === opt.key;
                return (
                  <button
                    key={opt.key}
                    style={{
                      ...sx.printerOption,
                      ...(active ? sx.printerOptionActive : null),
                    }}
                    onClick={() => { setPrinterOption(opt.key); setAppliedCoupon(null); }}
                  >
                    <span>{opt.label}</span>
                    <span style={sx.printerPrice}>
                      {opt.key === "app"
                        ? selectedPrice?.amount
                          ? formatINR(selectedPrice.amount)
                          : "Select plan"
                        : formatINR(opt.amount)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {couponsEnabled && selectedPrice ? (
            <div style={sx.couponCard}>
              <div style={sx.printerTitle}>Have a coupon code?</div>
              <div style={sx.couponRow}>
                <input style={sx.couponInput} value={couponCode} onChange={(event) => { setCouponCode(event.target.value.toUpperCase()); setAppliedCoupon(null); }} placeholder="Enter coupon code" />
                <button style={sx.couponButton} onClick={applyCoupon} disabled={couponBusy}>{couponBusy ? "Applying…" : "Apply"}</button>
              </div>
              {appliedCoupon ? <div style={sx.couponApplied}>{appliedCoupon.code} applied — you save {formatINR(appliedCoupon.discount)}</div> : null}
            </div>
          ) : null}
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.featureComparison}</div>
            <div style={sx.sectionSub}>{COPY.featureComparisonSub}</div>
          </div>

          <div style={sx.compareCard}>
            {featureMatrix.map((row) => (
              <div key={row.title} style={sx.compareRow}>
                <div style={sx.compareLabel}>✨ {row.title}</div>
                <div style={sx.compareBadges}>
                  <span style={{ ...sx.compareBadge, ...(row.basic ? sx.badgeOn : sx.badgeOff) }}>
                    {row.basic ? "✔" : "✕"} Basic
                  </span>
                  <span style={{ ...sx.compareBadge, ...(row.starter ? sx.badgeOnDark : sx.badgeOff) }}>
                    {row.starter ? "✔" : "✕"} Starter
                  </span>
                  <span style={{ ...sx.compareBadge, ...(row.professional ? sx.badgeOnGold : sx.badgeOff) }}>
                    {row.professional ? "✔" : "✕"} Professional
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={sx.section}>
          <div style={sx.sectionHeader}>
            <div style={sx.sectionTitle}>{COPY.execution}</div>
            <div style={sx.sectionSub}>{COPY.executionSub}</div>
          </div>

          <div style={sx.featuresGrid}>
            {features.map((feature) => (
              <div key={feature.key} style={sx.featureCard}>
                <div style={sx.featureIcon}>{feature.icon}</div>
                <div style={sx.featureTitle}>{feature.title}</div>
                <div style={sx.featureSub}>{feature.subtitle}</div>
              </div>
            ))}
          </div>

          <div style={sx.customCard}>
            <div style={sx.customHeader}>🛠️ {COPY.customWorkflows}</div>
            <div style={sx.customBody}>{COPY.customBody}</div>
            <button style={sx.customBtn} onClick={() => sendWhatsApp(COPY.customWorkflows)}>
              {COPY.requestCustomBuild} →
            </button>
          </div>
        </section>
      </div>

      {selectedPrice ? (
        <div style={sx.payBarWrap}>
          <div style={sx.payBar}>
            <div>
              <div style={sx.payBarTop}>
                {selectedPrice.planName} • {selectedPrice.durationLabel}
              </div>
              <div style={sx.payBarAmount}>
                {formatINR(
                  Number(selectedPrice.amount || 0) +
                    Number(
                      printerOptions.find((p) => p.key === printerOption)
                        ?.amount || 0
                    ) - Number(appliedCoupon?.discount || 0)
                )}
              </div>
              <div style={sx.payBarMeta}>
                Printer:{" "}
                {printerOptions.find((p) => p.key === printerOption)?.label ||
                  "App Only"}
              </div>
            </div>
            <div style={sx.payActions}>
              <button
                style={sx.clearBtn}
                onClick={() => {
                  setSelectedPrice(null);
                  setPrinterOption("app");
                }}
                title="Clear selection"
              >
                ⟲
              </button>
              <button
                style={sx.payBtn}
                onClick={() => {
                  const printer =
                    printerOptions.find((p) => p.key === printerOption) || printerOptions[0];
                  setSelectedPrice((prev) =>
                    prev
                      ? {
                          ...prev,
                          printerLabel: printer.label,
                          printerAmount: printer.amount,
                        }
                      : prev
                  );
                  setPayModalOpen(true);
                }}
              >
               Continue on WhatsApp
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <PaymentModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        selectedPrice={selectedPrice}
        onWhatsApp={sendSelectedPlanOnWhatsApp}
        discount={appliedCoupon?.discount || 0}
        couponCode={appliedCoupon?.code || ""}
      />
    </div>
  );
};

const sx = {
  page: {
    minHeight: "100vh",
    background: "radial-gradient(900px 420px at 0% -10%, rgba(1,91,154,.14), transparent 60%), #f4f7fb",
    color: "#0f172a",
    fontFamily: "Sora, \"Space Grotesk\", \"DM Sans\", system-ui, sans-serif",
    padding: 16,
    position: "relative",
    overflowX: "hidden",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100vw",
  },
  container: {
    width: "min(1180px, 100%)",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    margin: "0 auto",
  },
  glowOne: {
    position: "absolute",
    top: -80,
    right: -60,
    width: 220,
    height: 220,
    background: "#93c5fd",
    opacity: 0.14,
    borderRadius: 160,
  },
  glowTwo: {
    position: "absolute",
    bottom: -80,
    left: -60,
    width: 260,
    height: 260,
    background: "#bae6fd",
    opacity: 0.14,
    borderRadius: 200,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "-16px -16px 18px",
    padding: "16px 22px",
    background: "linear-gradient(135deg, #015B9A, #034A7A)",
    color: "#fff",
    boxShadow: "0 10px 24px rgba(1,91,154,.2)",
  },
  headerBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "rgba(255,255,255,.16)",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 800,
  },
  headerTitle: { fontSize: 18, fontWeight: 900 },
  headerSub: { fontSize: 12, color: "#dbeafe" },

  hero: {
    borderRadius: 20,
    padding: 24,
    marginBottom: 18,
    background: "linear-gradient(135deg, #015B9A, #034A7A)",
    border: "1px solid #015B9A",
    boxShadow: "0 22px 44px rgba(1,91,154,.2)",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  heroTop: { display: "flex", gap: 10, flexWrap: "wrap" },
  heroBadge: {
    background: "#ffffff",
    color: GOLD,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 12,
  },
  heroPill: {
    border: "1px solid rgba(15,23,42,0.12)",
    padding: "6px 10px",
    borderRadius: 999,
    color: "#0f172a",
    fontSize: 12,
    background: "rgba(255,255,255,.1)",
    color: "#fff",
  },
  heroTitle: { fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 900, marginTop: 14, color: "#fff", lineHeight: 1.2, overflowWrap: "anywhere" },
  heroBody: { color: "#dbeafe", marginTop: 8, lineHeight: 1.5, maxWidth: 820, overflowWrap: "anywhere" },
  heroNote: {
    marginTop: 10,
    padding: "8px 10px",
    borderRadius: 12,
    background: "rgba(255,255,255,.1)",
    border: "1px dashed rgba(255,255,255,.35)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
  },
  heroActions: { display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" },
  primaryCta: {
    background: "#ffffff",
    border: "none",
    color: "#015B9A",
    fontWeight: 800,
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
  },
  secondaryCta: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,.6)",
    color: "#fff",
    fontWeight: 700,
    padding: "10px 14px",
    borderRadius: 12,
    cursor: "pointer",
  },

  section: { marginTop: 18 },
  sectionHeader: { marginBottom: 10 },
  sectionTitle: { fontSize: 17, fontWeight: 900, color: "#0f172a" },
  sectionSub: { fontSize: 12, color: "#64748b" },

  tabsWrap: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: 10,
    width: "100%",
    minWidth: 0,
  },
  tabPill: {
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  tabPillActive: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: "#fff",
    border: "none",
  },

  pricingCard: {
    marginTop: 12,
    padding: 16,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 18px 36px rgba(15,23,42,.10)",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  printerCard: {
    marginTop: 12,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 12px 26px rgba(15,23,42,.08)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  printerTitle: { fontWeight: 900, fontSize: 14 },
  printerSub: { color: "#64748b", fontSize: 12, marginTop: 4 },
  printerHint: {
    marginTop: 6,
    fontSize: 12,
    color: "#475569",
    fontWeight: 700,
  },
  printerOptions: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
  },
  printerOption: {
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#f8fafc",
    borderRadius: 12,
    padding: "10px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 700,
    cursor: "pointer",
  },
  printerOptionActive: {
    background: "#eaf4fb",
    borderColor: GOLD,
  },
  printerPrice: { fontWeight: 800, color: "#0f172a" },
  couponCard: { marginTop: 12, padding: 14, borderRadius: 16, border: "1px solid rgba(15,23,42,0.08)", background: "#fff" },
  couponRow: { display: "flex", gap: 8, marginTop: 10 },
  couponInput: { flex: 1, minWidth: 0, border: "1px solid #cbd5e1", borderRadius: 10, padding: "10px 12px", fontWeight: 700 },
  couponButton: { border: 0, borderRadius: 10, padding: "10px 16px", background: GOLD_DEEP, color: "#fff", fontWeight: 800, cursor: "pointer" },
  couponApplied: { marginTop: 8, color: "#15803d", fontSize: 12, fontWeight: 800 },
  pricingTop: { display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" },
  pricingPlan: { fontSize: 16, fontWeight: 800 },
  pricingBase: { fontSize: 12, color: "#64748b", marginTop: 4 },
  bestValue: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: DARK,
    padding: "6px 10px",
    borderRadius: 999,
    fontWeight: 800,
    fontSize: 12,
  },
  pricingHead: {
    display: "flex",
    justifyContent: "space-between",
    color: "#64748b",
    fontSize: 12,
    marginTop: 14,
  },
  pricingRows: { marginTop: 8, display: "grid", gap: 8 },
  extensionWrap: { marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(15,23,42,0.08)" },
  priceRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 12,
    padding: "10px 12px",
    cursor: "pointer",
    color: "#0f172a",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  },
  priceRowSelected: {
    border: `1px solid ${GOLD}`,
    background: "#eaf4fb",
    color: "#0f172a",
  },
  priceLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 },
  radio: { color: GOLD, fontWeight: 900 },
  priceDuration: { fontWeight: 700 },
  priceRight: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, whiteSpace: "nowrap" },
  priceAmount: { fontWeight: 900, color: "#015B9A", whiteSpace: "nowrap" },
  originalAmount: { textDecoration: "line-through", color: "#94a3b8", fontSize: 12 },
  chev: { color: "#94a3b8" },
  chipPopular: {
    background: "rgba(148,163,184,0.25)",
    color: "#475569",
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
  },
  chipBest: {
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: "#fff",
    padding: "2px 6px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
  },

  compareCard: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 14px 30px rgba(15,23,42,.06)",
  },
  compareRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid rgba(15,23,42,0.08)",
  },
  compareLabel: { fontWeight: 600, color: "#0f172a" },
  compareBadges: { display: "flex", gap: 6, flexWrap: "wrap" },
  compareBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  badgeOn: { background: "#eaf4fb", color: GOLD },
  badgeOnDark: { background: GOLD, color: "#fff" },
  badgeOnGold: { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`, color: "#fff" },
  badgeOff: { background: "rgba(148,163,184,0.2)", color: "#64748b" },

  featuresGrid: {
    marginTop: 10,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  featureCard: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#ffffff",
    boxShadow: "0 10px 22px rgba(15,23,42,.06)",
  },
  featureIcon: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: "#eaf4fb",
    display: "grid",
    placeItems: "center",
    fontSize: 18,
  },
  featureTitle: { fontWeight: 800, marginTop: 8, color: "#0f172a" },
  featureSub: { color: "#64748b", fontSize: 12, marginTop: 4 },

  customCard: {
    marginTop: 14,
    padding: 16,
    borderRadius: 18,
    background: "linear-gradient(135deg, #eaf4fb, #fff)",
    border: "1px solid #bfdbfe",
  },
  customHeader: { fontWeight: 900, color: GOLD_DEEP },
  customBody: { color: "#475569", marginTop: 6, fontSize: 13 },
  customBtn: {
    marginTop: 12,
    background: GOLD,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 800,
    cursor: "pointer",
  },

  payBarWrap: {
    position: "fixed",
    bottom: 16,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    padding: "0 16px",
    zIndex: 50,
  },
  payBar: {
    width: "min(920px, 100%)",
    background: "linear-gradient(135deg, #015B9A, #034A7A)",
    border: "1px solid #015B9A",
    borderRadius: 16,
    padding: "10px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  payBarTop: { color: "#dbeafe", fontSize: 12 },
  payBarMeta: { color: "#bfdbfe", fontSize: 12, marginTop: 2 },
  payBarAmount: { fontSize: 20, fontWeight: 900, color: "#ffffff" },
  payActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    justifyContent: "center",
  },
  clearBtn: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.08)",
    color: "#e2e8f0",
    cursor: "pointer",
    fontWeight: 800,
  },
  payBtn: {
    background: "#fff",
    border: "none",
    color: GOLD,
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 900,
    cursor: "pointer",
  },

  pmOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(2,6,23,0.55)",
    display: "grid",
    placeItems: "center",
    padding: 16,
    zIndex: 60,
  },
  pmSheet: {
    width: "min(560px, 100%)",
    background: "#ffffff",
    borderRadius: 22,
    border: "1px solid #bfdbfe",
    padding: 18,
    boxShadow: "0 22px 50px rgba(15,23,42,.22)",
  },
  pmHandle: {
    width: 44,
    height: 5,
    borderRadius: 999,
    background: "rgba(15,23,42,0.12)",
    margin: "0 auto 10px",
  },
  pmHeader: { display: "flex", gap: 10, alignItems: "center" },
  pmIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: "#eaf4fb",
    display: "grid",
    placeItems: "center",
  },
  pmTitle: { fontWeight: 800, color: "#0f172a" },
  pmSub: { color: "#64748b", fontSize: 12 },
  pmClose: {
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,0.12)",
    color: "#0f172a",
    borderRadius: 10,
    width: 30,
    height: 30,
    cursor: "pointer",
  },
  pmSummary: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  pmPlan: { fontWeight: 800, color: "#0f172a" },
  pmMeta: { color: "#64748b", fontSize: 12 },
  pmAmountWrap: { textAlign: "right" },
  pmAmountLabel: { color: "#64748b", fontSize: 12 },
  pmAmount: { fontSize: 20, fontWeight: 900, color: "#0f172a" },
  pmPrimaryBtn: {
    width: "100%",
    border: "none",
    borderRadius: 12,
    padding: "12px 14px",
    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DEEP})`,
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  pmBtnRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 },
  pmSecondaryBtn: {
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 12,
    padding: "10px",
    fontWeight: 700,
    cursor: "pointer",
  },
  pmGhostBtn: {
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#f8fafc",
    color: "#64748b",
    borderRadius: 12,
    padding: "10px",
    fontWeight: 700,
    cursor: "pointer",
  },
};

if (!document.getElementById("plan-showcase-styles")) {
  const styleTag = document.createElement("style");
  styleTag.id = "plan-showcase-styles";
  styleTag.innerHTML = `
    @media (max-width: 720px){
      .plan-paybar-hidden{ display:none; }
    }
  `;
  document.head.appendChild(styleTag);
}

export default PlanShowcase;
